// slices/superAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/superadmin`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('accessToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// ---- Stats ----
export const fetchPlatformStats = createAsyncThunk(
  'superAdmin/fetchStats',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${BASE_URL}/stats/`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch stats');
    }
  }
);

// ---- Accounts (schools) ----
export const fetchAccounts = createAsyncThunk(
  'superAdmin/fetchAccounts',
  async (filters = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
      const response = await axios.get(`${BASE_URL}/accounts/?${params.toString()}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch accounts');
    }
  }
);

export const createAccount = createAsyncThunk(
  'superAdmin/createAccount',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}/accounts/`, data, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to create account');
    }
  }
);

export const updateAccount = createAsyncThunk(
  'superAdmin/updateAccount',
  async ({ id, ...data }, thunkAPI) => {
    try {
      const response = await axios.patch(`${BASE_URL}/accounts/${id}/`, data, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update account');
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'superAdmin/deleteAccount',
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/accounts/${id}/`, { headers: getAuthHeaders() });
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to delete account');
    }
  }
);

// ---- Users (across all tenants) ----
export const fetchAllUsers = createAsyncThunk(
  'superAdmin/fetchAllUsers',
  async (filters = {}, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (filters.account) params.append('account', filters.account);
      if (filters.role) params.append('role', filters.role);
      if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
      if (filters.search) params.append('search', filters.search);
      const response = await axios.get(`${BASE_URL}/users/?${params.toString()}`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch users');
    }
  }
);

export const createPlatformUser = createAsyncThunk(
  'superAdmin/createPlatformUser',
  async (data, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}/users/`, data, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to create user');
    }
  }
);

export const deletePlatformUser = createAsyncThunk(
  'superAdmin/deletePlatformUser',
  async (id, thunkAPI) => {
    try {
      await axios.delete(`${BASE_URL}/users/${id}/`, { headers: getAuthHeaders() });
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to delete user');
    }
  }
);

export const toggleUserActive = createAsyncThunk(
  'superAdmin/toggleUserActive',
  async (id, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}/users/${id}/toggle-active/`, {}, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to toggle user status');
    }
  }
);

export const resetUserPassword = createAsyncThunk(
  'superAdmin/resetUserPassword',
  async ({ id, new_password }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/users/${id}/reset-password/`,
        { new_password },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to reset password');
    }
  }
);

// ---- Platform settings ----
export const fetchPlatformSettings = createAsyncThunk(
  'superAdmin/fetchPlatformSettings',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/`, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to fetch settings');
    }
  }
);

export const updatePlatformSettings = createAsyncThunk(
  'superAdmin/updatePlatformSettings',
  async (data, thunkAPI) => {
    try {
      const response = await axios.patch(`${BASE_URL}/settings/`, data, { headers: getAuthHeaders() });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update settings');
    }
  }
);

export const bulkUpdateAccounts = createAsyncThunk(
  'superAdmin/bulkUpdateAccounts',
  async ({ ids, changes }, thunkAPI) => {
    try {
      const response = await axios.post(`${BASE_URL}/accounts/bulk-update/`, { ids, changes }, { headers: getAuthHeaders() });
      return { ...response.data, ids, changes };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || 'Failed to update accounts');
    }
  }
);

const superAdminSlice = createSlice({
  name: 'superAdmin',
  initialState: {
    stats: null,
    settings: null,
    accounts: [],
    users: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearSuperAdminError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Stats
      .addCase(fetchPlatformStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      // Accounts
      .addCase(fetchAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        state.accounts.unshift(action.payload);
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        const index = state.accounts.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) state.accounts[index] = action.payload;
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.accounts = state.accounts.filter((a) => a.id !== action.payload);
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Users
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createPlatformUser.fulfilled, (state, action) => {
        state.users.unshift(action.payload);
      })
      .addCase(createPlatformUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deletePlatformUser.fulfilled, (state, action) => {
        state.users = state.users.filter((u) => u.id !== action.payload);
      })
      .addCase(deletePlatformUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(toggleUserActive.fulfilled, (state, action) => {
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) state.users[index] = action.payload;
      })
      .addCase(toggleUserActive.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Platform settings
      .addCase(fetchPlatformSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(fetchPlatformSettings.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updatePlatformSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
        if (state.stats) {
          state.stats.maintenance_mode = action.payload.maintenance_mode;
          state.stats.announcement_enabled = action.payload.announcement_enabled;
        }
      })
      .addCase(updatePlatformSettings.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(bulkUpdateAccounts.fulfilled, (state, action) => {
        const { ids, changes } = action.payload;
        state.accounts.forEach((a) => {
          if (ids.includes(a.id)) Object.assign(a, changes);
        });
      })
      .addCase(bulkUpdateAccounts.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearSuperAdminError } = superAdminSlice.actions;
export default superAdminSlice.reducer;
